<template>
  <div class="list-edit-container">
    <div class="list-edit-header">
      <div class="header-content">
        <button class="btn btn-outline-secondary btn-sm me-3" @click="goBack">
          <i class="fas fa-arrow-left me-2"></i>{{ $t('Back') }}
        </button>
        <div class="header-info">
          <h2 class="page-title">
            <i class="fas fa-sitemap me-2"></i>
            {{ list?.name || $t('Edit list') }}
          </h2>
          <span v-if="list" class="list-count-badge">
            {{ getListCountOptimized(list) }} {{ $t('items') }}
          </span>
          <div v-if="!isLoadingUsageCounts && usageStats.total > 0" class="usage-stats-badges">
            <span class="usage-badge used" :title="`${usageStats.used} ${$t('items in use')}`">
              <i class="fas fa-link"></i> {{ usageStats.used }}
            </span>
            <span class="usage-badge unused" :title="`${usageStats.unused} ${$t('unused items')}`">
              <i class="fas fa-unlink"></i> {{ usageStats.unused }}
            </span>
          </div>
        </div>
        <div class="header-actions">
          <div v-if="hasUnsavedChanges" class="unsaved-indicator me-3">
            <i class="fas fa-exclamation-triangle text-warning me-1"></i>
            <span class="text-warning">{{ $t('Pending changes') }}</span>
          </div>
          <button
            v-if="hasUnsavedChanges"
            class="btn btn-success btn-sm"
            @click="saveChanges"
            :disabled="isUpdating"
          >
            <i v-if="isUpdating" class="fas fa-spinner fa-spin me-1"></i>
            <i v-else class="fas fa-save me-1"></i>
            {{ isUpdating ? $t("Saving...") : $t("Save Changes") }}
          </button>
        </div>
      </div>
    </div>

    <div class="list-edit-body" v-if="list">
      <!-- Área del árbol jerárquico -->
      <div class="tree-section">
        <div class="tree-header">
          <div class="tree-header-top">
            <h4><i class="fas fa-sitemap me-2"></i>{{ $t('Hierarchical structure') }}</h4>
            <div class="usage-filter-container">
              <label class="filter-label">
                <i class="fas fa-filter me-1"></i>{{ $t('Filter') }}:
              </label>
              <div class="btn-group btn-group-sm" role="group">
                <button 
                  type="button" 
                  class="btn"
                  :class="usageFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="usageFilter = 'all'"
                >
                  {{ $t('All') }}
                </button>
                <button 
                  type="button" 
                  class="btn"
                  :class="usageFilter === 'used' ? 'btn-success' : 'btn-outline-secondary'"
                  @click="usageFilter = 'used'"
                  :disabled="isLoadingUsageCounts"
                >
                  <i class="fas fa-link me-1"></i>{{ $t('In use') }}
                </button>
                <button 
                  type="button" 
                  class="btn"
                  :class="usageFilter === 'unused' ? 'btn-warning' : 'btn-outline-secondary'"
                  @click="usageFilter = 'unused'"
                  :disabled="isLoadingUsageCounts"
                >
                  <i class="fas fa-unlink me-1"></i>{{ $t('Unused') }}
                </button>
              </div>
              <button 
                class="btn btn-sm btn-outline-secondary ms-2"
                @click="loadUsageCounts"
                :disabled="isLoadingUsageCounts"
                :title="$t('Refresh usage counts')"
              >
                <i class="fas" :class="isLoadingUsageCounts ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
              </button>
              <button 
                class="btn btn-sm btn-outline-danger ms-2"
                @click="deleteUnusedItems"
                :disabled="isLoadingUsageCounts || isDeletingUnused || usageStats.unused === 0"
                :title="usageStats.unused > 0 ? $t('Delete unused items') + ` (${usageStats.unused})` : $t('No unused items')"
              >
                <i class="fas" :class="isDeletingUnused ? 'fa-spinner fa-spin' : 'fa-trash-alt'"></i>
                <span class="ms-1">{{ $t('Delete unused') }}</span>
                <span v-if="usageStats.unused > 0" class="badge bg-danger ms-1">{{ usageStats.unused }}</span>
              </button>
              <button
                v-if="canMerge"
                class="btn btn-sm btn-outline-primary ms-2"
                @click="showMergeModal = true"
                :disabled="isLoadingUsageCounts"
                :title="$t('listMerge.buttonTitle')"
              >
                <i class="fas fa-object-group"></i>
                <span class="ms-1">{{ $t('listMerge.button') }}</span>
              </button>
              <button
                v-if="canMerge && lastMerge"
                class="btn btn-sm btn-outline-warning ms-2"
                @click="undoLastMerge"
                :disabled="isUndoingMerge"
                :title="$t('listMerge.undoTitle')"
              >
                <i class="fas" :class="isUndoingMerge ? 'fa-spinner fa-spin' : 'fa-undo'"></i>
                <span class="ms-1">{{ $t('listMerge.undoButton') }}</span>
              </button>
              <!-- Botón para restaurar backup -->
              <button 
                v-if="showRestoreOption"
                class="btn btn-sm btn-warning ms-2"
                @click="restoreBackup"
                :disabled="isRestoring"
                :title="$t('Restore previous version')"
              >
                <i class="fas" :class="isRestoring ? 'fa-spinner fa-spin' : 'fa-undo'"></i>
                <span class="ms-1">{{ $t('Undo') }}</span>
              </button>
            </div>
          </div>
          <p class="tree-help">
            {{ $t('Drag items to create hierarchies. Use the') }}
            <i class="fas fa-grip-lines"></i> {{ $t('icon to move items.') }}
            <span v-if="!isLoadingUsageCounts" class="tree-help-usage">
              <span class="badge bg-success">{{ $t('Green') }}</span> {{ $t('items are in use,') }} 
              <span class="badge bg-warning text-dark">{{ $t('yellow') }}</span> {{ $t('items have no related content.') }}
            </span>
          </p>
        </div>
        <div v-if="isLoadingUsageCounts" class="loading-usage-overlay">
          <div class="spinner-border text-primary spinner-border-sm" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <span class="ms-2">Calculando uso de elementos...</span>
        </div>
        <div class="tree-content-wrapper" v-if="list.type">
          <tree-edit
            :nodes="filteredListValues"
            :listId="list.type"
            :usageCounts="usageCounts"
            :usageSources="usageSources"
            :showAccountingCode="isExpenseTypeList"
            :showConversionRate="isCurrencyList"
            @update="
              (newTree, operationType) =>
                onTreeUpdate(list, newTree, operationType)
            "
            @save-requested="saveChanges"
            @backend-delete="onBackendDelete"
          />
        </div>
      </div>

      <!-- Panel de acciones -->
      <div v-if="canEdit" class="actions-section">
        <!-- Agregar elemento -->
        <div class="action-card">
          <h5><i class="fas fa-plus me-2"></i>{{ $t('Add item') }}</h5>
          <div class="mb-3">
            <label class="form-label">{{ $t('Parent term (optional)') }}</label>
            <select
              v-model="selectedParent"
              class="form-select"
              :disabled="!list.values || list.values.length === 0"
            >
              <option value="">{{ $t('No parent term (root item)') }}</option>
              <option
                v-for="parent in getParentOptions(list)"
                :key="parent._id || parent.label"
                :value="parent._id || parent.label"
              >
                {{ parent.label }}
              </option>
            </select>
            <small class="form-text text-muted">
              {{ $t('Select a parent term to create a child item') }}
            </small>
          </div>
          <div class="input-group">
            <input
              v-model="newValue"
              class="form-control"
              :placeholder="$t('New item name')"
              @keyup.enter="addValue"
            />
            <button class="btn btn-success" @click="addValue">
              <i class="fas fa-plus me-1"></i>{{ $t('Add') }}
            </button>
          </div>
        </div>

        <!-- Importar elementos -->
        <div class="action-card">
          <h5><i class="fas fa-upload me-2"></i>{{ $t('Import items') }}</h5>
          <textarea
            v-model="list.valuesText"
            class="form-control mb-2"
            :placeholder="$t('One term per line (use - for children, -- for grandchildren, etc.)')"
            rows="8"
          ></textarea>
          <button class="btn btn-success w-100" @click="importAllFromText">
            <i class="fas fa-download me-1"></i>{{ $t('Import') }}
          </button>
        </div>
      </div>
    </div>

    <list-merge-modal
      v-if="list"
      :visible="showMergeModal"
      :list-id="list._id"
      :list-name="list.name"
      :values="list.values"
      :usage-counts="usageCounts"
      @close="showMergeModal = false"
      @merged="onMerged"
    />

    <!-- Loading state -->
    <ContentSkeleton v-else variant="list" :rows="8" padded :label="$t('Loading list...')" />
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import TreeEdit from "@/components/TreeEdit.vue";
import ListMergeModal from "@/components/gotrading/lists/ListMergeModal.vue";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import permissionsMixin from "@/mixins/permissionsMixin";
import Swal from "sweetalert2";
import { normalizeHexColor } from "@/utils/listItemColor";

export default {
  name: "ListEdit",
  components: {
    ContentSkeleton,
    TreeEdit,
    ListMergeModal,
  },
  mixins: [permissionsMixin],
  data() {
    return {
      list: null,
      newValue: "",
      selectedParent: "",
      hasUnsavedChanges: false,
      isUpdating: false,
      listCountCache: {},
      usageCounts: {},
      usageSources: {},
      isLoadingUsageCounts: false,
      usageFilter: "all", // "all", "used", "unused"
      isDeletingUnused: false,
      // Backup para poder restaurar después de eliminar
      lastDeletedBackup: null,
      showRestoreOption: false,
      isRestoring: false,
      // Unificación de términos
      showMergeModal: false,
      lastMerge: null,
      isUndoingMerge: false,
    };
  },
  computed: {
    canEdit() {
      return (
        this.hasPermission("lists.view") ||
        this.hasPermission("lists.manage") ||
        this.hasPermission("lists.delete")
      );
    },
    // Unificar reescribe registros de todos los modelos: el backend exige lists.manage
    canMerge() {
      return this.hasPermission("lists.manage");
    },
    isExpenseTypeList() {
      // Mostrar campo de código contable para listas de tipos de gastos
      if (!this.list) return false;
      const listType = (this.list.type || '').toLowerCase();
      const listName = (this.list.name || '').toLowerCase();
      return listType.includes('expense') || 
             listType.includes('cost') ||
             listName.includes('expense') ||
             listName.includes('cost') ||
             listName.includes('gasto') ||
             listName.includes('costo');
    },
    isCurrencyList() {
      // Mostrar campo de coeficiente de conversión para listas de monedas
      if (!this.list) return false;
      const listType = (this.list.type || '').toLowerCase();
      const listName = (this.list.name || '').toLowerCase();
      return listType.includes('currency') || 
             listType.includes('moneda') ||
             listName.includes('currency') ||
             listName.includes('moneda') ||
             listName.includes('divisa');
    },
    usageStats() {
      if (!this.list || !this.list.values) return { total: 0, used: 0, unused: 0 };
      
      let used = 0;
      let unused = 0;
      
      // Helper function to check if a node or any of its descendants is used
      const isNodeOrDescendantUsed = (node) => {
        const nodeId = node._id?.toString() || node._id;
        const count = this.usageCounts[nodeId] || 0;
        if (count > 0) return true;
        
        // Check if any child is used
        if (node.children && node.children.length > 0) {
          return node.children.some(child => isNodeOrDescendantUsed(child));
        }
        
        return false;
      };
      
      const countNodes = (nodes) => {
        nodes.forEach(node => {
          if (isNodeOrDescendantUsed(node)) {
            used++;
          } else {
            unused++;
          }
          if (node.children && node.children.length > 0) {
            countNodes(node.children);
          }
        });
      };
      
      countNodes(this.list.values);
      
      return { total: used + unused, used, unused };
    },
    filteredListValues() {
      if (!this.list || !this.list.values || this.usageFilter === "all") {
        return this.list?.values || [];
      }
      
      // Helper function to check if a node or any of its descendants is used
      const isNodeOrDescendantUsed = (node) => {
        const nodeId = node._id?.toString() || node._id;
        const count = this.usageCounts[nodeId] || 0;
        if (count > 0) return true;
        
        // Check if any child is used
        if (node.children && node.children.length > 0) {
          return node.children.some(child => isNodeOrDescendantUsed(child));
        }
        
        return false;
      };
      
      // Filter function that preserves tree structure
      const filterTree = (nodes) => {
        return nodes.reduce((acc, node) => {
          // Check if node or any descendant is used (check BEFORE filtering children)
          const isUsed = isNodeOrDescendantUsed(node);
          
          // Filter children first
          const filteredChildren = node.children && node.children.length > 0 
            ? filterTree(node.children) 
            : [];
          
          // For "unused" filter: only include if node is NOT used (and all descendants are NOT used)
          if (this.usageFilter === "unused") {
            // Only include if the node itself and all its descendants are unused
            if (!isUsed) {
              // Include the node, even if it has no children or all children were filtered
              acc.push({
                ...node,
                children: filteredChildren
              });
            }
            // If isUsed is true, don't include the node at all
          } 
          // For "used" filter: include if node is used OR has used children (shown in filteredChildren)
          else if (this.usageFilter === "used") {
            if (isUsed || filteredChildren.length > 0) {
              acc.push({
                ...node,
                children: filteredChildren
              });
            }
          }
          
          return acc;
        }, []);
      };
      
      return filterTree(this.list.values);
    },
  },
  async mounted() {
    await this.fetchUserPermissions();
    await this.loadList();
    await this.loadUsageCounts();
    await this.loadLastMerge();
  },
  methods: {
    async loadLastMerge() {
      if (!this.list || !this.list._id || !this.canMerge) return;
      try {
        const { data } = await wencesApi.get(`/api/gotrading/lists/${this.list._id}/merge/last`);
        this.lastMerge = data.lastMerge || null;
      } catch (error) {
        console.error("Error loading last merge:", error);
        this.lastMerge = null;
      }
    },
    async onMerged() {
      await this.loadList();
      await this.loadUsageCounts();
      await this.loadLastMerge();
    },
    async undoLastMerge() {
      if (!this.lastMerge) return;
      const escape = (text) =>
        String(text).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
      const groupsHtml = this.lastMerge.groups
        .map((g) => `<li>${g.sources.map(escape).join(", ")} → <strong>${escape(g.target)}</strong></li>`)
        .join("");
      const when = new Date(this.lastMerge.createdAt).toLocaleString();
      const result = await Swal.fire({
        title: this.$tn("listMerge.undoConfirmTitle"),
        html: `
          <p>${escape(this.$tn("listMerge.undoConfirmWho", { when, who: this.lastMerge.createdByEmail || "—" }))}</p>
          <ul class="text-start small">${groupsHtml}</ul>
          <p class="small text-muted">${escape(this.$tn("listMerge.undoConfirmNote"))}</p>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ffc107",
        cancelButtonColor: "#6c757d",
        confirmButtonText: this.$tn("listMerge.undoConfirmButton"),
        cancelButtonText: this.$tn("Cancel"),
      });
      if (!result.isConfirmed) return;

      this.isUndoingMerge = true;
      try {
        const { data } = await wencesApi.post(
          `/api/gotrading/lists/${this.list._id}/merge/${this.lastMerge.logId}/undo`
        );
        await this.onMerged();
        Swal.fire({
          title: this.$tn("listMerge.undoDoneTitle"),
          text: data.conflicts
            ? this.$tn("listMerge.undoDoneWithConflicts", { restored: data.restored, conflicts: data.conflicts })
            : this.$tn("listMerge.undoDone", { restored: data.restored }),
          icon: data.conflicts ? "warning" : "success",
        });
      } catch (error) {
        Swal.fire({
          title: this.$tn("Error"),
          text: error.response?.data?.message || error.message,
          icon: "error",
        });
      } finally {
        this.isUndoingMerge = false;
      }
    },
    async loadList() {
      try {
        const listId = this.$route.params.id;
        const { data: lists } = await wencesApi.get("/api/gotrading/lists");
        const foundList = lists.find((l) => l._id === listId || l.type === listId);
        
        if (!foundList) {
          Swal.fire({
            title: "Error",
            text: "Listado no encontrado",
            icon: "error",
          }).then(() => {
            this.goBack();
          });
          return;
        }

        // Convertir plano a árbol
        foundList.values = this.planoATree(foundList.values);
        // La cuenta cacheada es del listado anterior (borrar sin usar o unificar la cambian)
        this.listCountCache = {};
        this.list = foundList;
      } catch (error) {
        console.error("Error loading list:", error);
        Swal.fire({
          title: "Error",
          text: "No se pudo cargar el listado",
          icon: "error",
        }).then(() => {
          this.goBack();
        });
      }
    },
    async loadUsageCounts() {
      if (!this.list || !this.list._id) return;
      
      this.isLoadingUsageCounts = true;
      try {
        const { data } = await wencesApi.get(`/api/gotrading/lists/${this.list._id}/usage-counts`);
        this.usageCounts = data.usageCounts || {};
        this.usageSources = data.usageSources || {};
      } catch (error) {
        console.error("Error loading usage counts:", error);
        this.usageCounts = {};
        this.usageSources = {};
      } finally {
        this.isLoadingUsageCounts = false;
      }
    },
    async deleteUnusedItems() {
      if (!this.list || this.usageStats.unused === 0) return;
      
      // Confirmar con el usuario
      const result = await Swal.fire({
        title: this.$tn('Delete unused items?'),
        html: `
          <p>${this.$tn('Items not being used in any model will be deleted.')}</p>
          <p><small class="text-muted"><i class="fas fa-info-circle me-1"></i>${this.$tn('Parent items with children in use will not be deleted.')}</small></p>
          <p class="text-success"><i class="fas fa-undo me-1"></i>${this.$tn('You can undo this action with the Undo button.')}</p>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: this.$tn('Yes, delete'),
        cancelButtonText: this.$tn('Cancel')
      });
      
      if (!result.isConfirmed) return;
      
      this.isDeletingUnused = true;
      try {
        // Guardar backup antes de eliminar
        this.lastDeletedBackup = JSON.parse(JSON.stringify(this.list.values));
        
        // Helper function to check if a node or any of its descendants is used
        const isNodeOrDescendantUsed = (node) => {
          const nodeId = node._id?.toString() || node._id;
          const count = this.usageCounts[nodeId] || 0;
          if (count > 0) return true;
          
          // Check if any child is used
          if (node.children && node.children.length > 0) {
            return node.children.some(child => isNodeOrDescendantUsed(child));
          }
          
          return false;
        };
        
        // Recopilar IDs de elementos sin usar (solo los que no tienen hijos usados)
        const unusedIds = [];
        const collectUnusedIds = (nodes) => {
          nodes.forEach(node => {
            const nodeId = node._id?.toString() || node._id;
            // Solo agregar si el nodo y todos sus descendientes no están siendo usados
            if (!isNodeOrDescendantUsed(node)) {
              unusedIds.push(nodeId);
            }
            if (node.children && node.children.length > 0) {
              collectUnusedIds(node.children);
            }
          });
        };
        collectUnusedIds(this.list.values);
        
        if (unusedIds.length === 0) {
          Swal.fire({
            title: this.$tn('No changes'),
            text: this.$tn('There are no unused items to delete.'),
            icon: 'info'
          });
          this.lastDeletedBackup = null;
          return;
        }
        
        // Llamar al backend para eliminar
        const { data } = await wencesApi.post(`/api/gotrading/lists/${this.list._id}/delete-unused`, {
          unusedIds
        });
        
        // Recargar el listado
        await this.loadList();
        await this.loadUsageCounts();
        
        // Mostrar opción de restaurar
        this.showRestoreOption = true;
        
        Swal.fire({
          title: this.$tn('Deleted!'),
          html: `
            <p>${this.$tn('Deleted')} <strong>${data.deletedCount || unusedIds.length}</strong> ${this.$tn('unused items')}.</p>
            <p class="text-muted"><small><i class="fas fa-info-circle me-1"></i>${this.$tn('Use the Undo button to restore if needed.')}</small></p>
          `,
          icon: 'success'
        });
        
      } catch (error) {
        console.error("Error deleting unused items:", error);
        this.lastDeletedBackup = null;
        Swal.fire({
          title: this.$tn('Error'),
          text: this.$tn('Could not delete items.') + ' ' + (error.response?.data?.message || error.message),
          icon: 'error'
        });
      } finally {
        this.isDeletingUnused = false;
      }
    },
    async restoreBackup() {
      if (!this.lastDeletedBackup || !this.list) return;
      
      const result = await Swal.fire({
        title: this.$tn('Restore previous version?'),
        text: this.$tn('The list will be restored to how it was before deleting items.'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#ffc107',
        cancelButtonColor: '#6c757d',
        confirmButtonText: this.$tn('Yes, restore'),
        cancelButtonText: this.$tn('Cancel')
      });
      
      if (!result.isConfirmed) return;
      
      this.isRestoring = true;
      try {
        // Restaurar los valores del backup
        await wencesApi.put(`/api/gotrading/lists/${this.list._id}`, {
          ...this.list,
          values: this.lastDeletedBackup
        });
        
        // Recargar el listado
        await this.loadList();
        await this.loadUsageCounts();
        
        // Limpiar backup y ocultar botón
        this.lastDeletedBackup = null;
        this.showRestoreOption = false;
        
        Swal.fire({
          title: this.$tn('Restored!'),
          text: this.$tn('The list has been restored to the previous version.'),
          icon: 'success'
        });
        
      } catch (error) {
        console.error("Error restoring backup:", error);
        Swal.fire({
          title: this.$tn('Error'),
          text: this.$tn('Could not restore the list.') + ' ' + (error.response?.data?.message || error.message),
          icon: 'error'
        });
      } finally {
        this.isRestoring = false;
      }
    },
    planoATree(values) {
      if (!values || !Array.isArray(values)) return [];
      const map = {};
      const labelToIdMap = {};
      const roots = [];

      // Primero creamos todos los nodos
      values.forEach((item) => {
        if (!item || typeof item !== "object" || !item.label) {
          console.warn("Item inválido encontrado:", item);
          return;
        }

        const nodeId = item._id || item.label;

        map[nodeId] = {
          ...item,
          _id: nodeId,
          children: [],
          expanded: false,
          accounting_code: item.accounting_code || null,
          conversionRate: item.conversionRate || null,
          color: item.color || null,
        };
        labelToIdMap[item.label] = nodeId;
      });

      // Luego establecemos las relaciones padre-hijo
      values.forEach((item) => {
        if (!item || typeof item !== "object" || !item.label) {
          return;
        }

        const nodeId = item._id || item.label;

        if (!map[nodeId]) {
          console.warn("Nodo no encontrado en el mapa:", nodeId);
          return;
        }

        if (item.parent) {
          const parentId = labelToIdMap[item.parent];
          if (parentId && map[parentId]) {
            map[parentId].children.push(map[nodeId]);
          } else {
            if (map[item.parent]) {
              map[item.parent].children.push(map[nodeId]);
            } else {
              console.warn(
                `Padre no encontrado para "${item.label}": "${item.parent}"`
              );
              roots.push(map[nodeId]);
            }
          }
        } else {
          roots.push(map[nodeId]);
        }
      });

      return roots;
    },
    treeAPlano(tree, parent = null) {
      if (!tree || !Array.isArray(tree)) return [];

      const plano = [];
      const stack = [];
      const MAX_ARRAY_SIZE = 1000000;

      tree.forEach((node) => {
        if (node && typeof node === "object") {
          stack.push({ node, parent });
        }
      });

      while (stack.length > 0) {
        const { node, parent } = stack.pop();

        if (!node || typeof node !== "object" || !node.label) {
          console.warn("Nodo inválido en treeAPlano:", node);
          continue;
        }

        const { _id, label, children } = node;

        if (plano.length >= MAX_ARRAY_SIZE) {
          console.warn(
            `Array size limit reached (${MAX_ARRAY_SIZE}). Truncating tree conversion.`
          );
          break;
        }

        const planoItem = {
          label,
        };

        if (_id && _id.length === 24 && /^[0-9a-fA-F]{24}$/.test(_id)) {
          planoItem._id = _id;
        }

        // Usar el parent del nodo si existe (puede haber sido editado), 
        // de lo contrario usar el parent calculado desde la estructura
        const nodeParent = node.parent !== undefined ? node.parent : parent;
        
        if (
          nodeParent &&
          nodeParent.length === 24 &&
          /^[0-9a-fA-F]{24}$/.test(nodeParent)
        ) {
          planoItem.parent = nodeParent;
        } else if (nodeParent && typeof nodeParent === "string") {
          planoItem.parent = nodeParent;
        } else {
          planoItem.parent = null;
        }

        // Incluir código contable si existe
        if (node.accounting_code) {
          planoItem.accounting_code = node.accounting_code;
        }

        // Color del ítem (cualquier lista): se elige en el modal «Editar elemento» y se
        // manda siempre, así quitarlo («sin color») también se persiste.
        planoItem.color = normalizeHexColor(node.color) || null;

        // Incluir coeficiente de conversión si existe
        if (node.conversionRate !== undefined && node.conversionRate !== null) {
          planoItem.conversionRate = node.conversionRate;
        }

        if (node.labels && typeof node.labels === "object" && !Array.isArray(node.labels)) {
          planoItem.labels = { ...node.labels };
        }

        plano.push(planoItem);

        if (children && Array.isArray(children) && children.length > 0) {
          children.forEach((child) => {
            if (child && typeof child === "object") {
              const parentId =
                _id && _id.length === 24 && /^[0-9a-fA-F]{24}$/.test(_id)
                  ? _id
                  : label;
              stack.push({ node: child, parent: parentId });
            }
          });
        }
      }

      return plano;
    },
    getListCount(list) {
      if (!list || !list.values || !Array.isArray(list.values)) return 0;

      let count = 0;
      const stack = [...list.values];

      while (stack.length > 0) {
        const item = stack.pop();

        if (!item || typeof item !== "object") {
          console.warn("Item inválido en getListCount:", item);
          continue;
        }

        count++;

        if (
          item.children &&
          Array.isArray(item.children) &&
          item.children.length > 0
        ) {
          stack.push(...item.children);
        }
      }

      return count;
    },
    getListCountOptimized(list) {
      const cacheKey = list._id;
      if (this.listCountCache[cacheKey] !== undefined) {
        return this.listCountCache[cacheKey];
      }

      const count = this.getListCount(list);
      this.listCountCache[cacheKey] = count;
      return count;
    },
    async updateList(skipRefresh = false) {
      try {
        const isLargeList = this.getListCount(this.list) > 1000;
        if (isLargeList) {
          Swal.fire({
            title: "Guardando...",
            text: "Procesando lista grande, por favor espera...",
            allowOutsideClick: false,
            showConfirmButton: false,
            willOpen: () => {
              Swal.showLoading();
            },
          });
        }

        const plano = this.treeAPlano(this.list.values);

        if (plano.length >= 1000000) {
          Swal.fire({
            title: "Lista muy grande",
            text: "La lista es demasiado grande para guardar completamente. Se guardará una versión truncada.",
            icon: "warning",
            confirmButtonText: "Continuar",
          });
        }

        await wencesApi.put(`/api/gotrading/lists/${this.list._id}`, {
          ...this.list,
          values: plano,
        });

        delete this.listCountCache[this.list._id];

        if (isLargeList) {
          Swal.fire({
            title: "¡Guardado!",
            text: `Se guardaron ${plano.length} elementos correctamente.`,
            icon: "success",
            timer: 2000,
          });
        }

        if (!skipRefresh) {
          await this.loadList();
        }
      } catch (error) {
        console.error("Error saving list:", error);
        Swal.fire({
          title: "Error al guardar",
          text: "Hubo un error al guardar la lista. Por favor, inténtalo de nuevo.",
          icon: "error",
        });
      }
    },
    onTreeUpdate(list, newTree, operationType) {
      list.values = newTree;

      if (operationType === "label-edit" || operationType === "drag-drop") {
        this.hasUnsavedChanges = true;
      }
    },
    onBackendDelete() {
      this.hasUnsavedChanges = false;
    },
    getParentOptions(list) {
      if (!list || !list.values) return [];
      return list.values.filter((item) => !item.isChild);
    },
    async addValue() {
      const value = this.newValue;
      if (!value) return;

      const parentId = this.selectedParent;
      const isChild = !!parentId;

      try {
        await wencesApi.post(`/api/gotrading/lists/${this.list.type}/values`, {
          label: value,
          parent: parentId,
          isChild: isChild,
        });

        await this.loadList();

        this.newValue = "";
        this.selectedParent = "";

        Swal.fire({
          title: this.$tn("Item added!"),
          text: `${this.$tn("Added")} "${value}" ${
            isChild ? this.$tn("as child item") : this.$tn("as root item")
          }`,
          icon: "success",
          timer: 2000,
        });
      } catch (error) {
        console.error("Error adding value:", error);
        Swal.fire({
          title: this.$tn("Error adding item"),
          text: this.$tn("There was an error adding the item. Please try again."),
          icon: "error",
        });
      }
    },
    async saveChanges() {
      if (!this.list || this.isUpdating) return;

      const itemCount = this.getListCount(this.list);
      if (itemCount > 50000) {
        const result = await Swal.fire({
          title: this.$tn("Very large list"),
          html: `
            <div class="text-start">
              <p>${this.$tn("This list has")} <strong>${itemCount.toLocaleString()}</strong> ${this.$tn("items")}.</p>
              <p>${this.$tn("Saving such a large list may take a long time and cause performance issues.")}</p>
              <p class="text-warning"><strong>${this.$tn("Recommendation")}:</strong> ${this.$tn("Consider splitting this list into smaller lists.")}</p>
            </div>
          `,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: this.$tn("Save anyway"),
          cancelButtonText: this.$tn("Cancel"),
          confirmButtonColor: "#d33",
        });

        if (!result.isConfirmed) {
          return;
        }
      }

      this.isUpdating = true;
      try {
        await this.updateList(true);
        this.hasUnsavedChanges = false;
        Swal.fire({
          title: this.$tn("Saved!"),
          text: this.$tn("Changes saved successfully."),
          icon: "success",
          timer: 2000,
        });
      } catch (error) {
        console.error("Error saving changes:", error);
        Swal.fire({
          title: this.$tn("Error saving"),
          text: this.$tn("There was an error saving the changes. Please try again."),
          icon: "error",
        });
      } finally {
        this.isUpdating = false;
      }
    },
    async importAllFromText() {
      if (!this.list.valuesText) return;

      try {
        Swal.fire({
          title: this.$tn("Processing..."),
          text: this.$tn("The backend is processing the list, please wait..."),
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            Swal.showLoading();
          },
        });

        const response = await wencesApi.post(
          `/api/gotrading/lists/${this.list._id}/import-text`,
          {
            text: this.list.valuesText,
          }
        );

        this.list.valuesText = "";
        this.hasUnsavedChanges = false;

        await this.loadList();

        Swal.fire({
          title: this.$tn("Import completed!"),
          text: this.$tn("Items imported successfully from the backend."),
          icon: "success",
          timer: 2000,
        });
      } catch (error) {
        console.error("Error during backend import:", error);
        Swal.fire({
          title: this.$tn("Import error"),
          text: this.$tn("There was an error processing the list in the backend. Please check the format and try again."),
          icon: "error",
        });
      }
    },
    goBack() {
      this.$router.push("/lists");
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges) {
      Swal.fire({
        title: this.$tn("Discard changes?"),
        text: this.$tn("You have unsaved changes. Are you sure you want to leave?"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$tn("Yes, discard"),
        cancelButtonText: this.$tn("Cancel"),
      }).then((result) => {
        if (result.isConfirmed) {
          next();
        } else {
          next(false);
        }
      });
    } else {
      next();
    }
  },
};
</script>

<style scoped>
.list-edit-container {
  min-height: 100vh;
  background: #f8f9fa;
  padding: 20px;
}

.list-edit-header {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-bottom: 24px;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.header-info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  color: #2c3e50;
  display: flex;
  align-items: center;
}

.list-count-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
}

.usage-stats-badges {
  display: flex;
  gap: 8px;
  margin-left: 12px;
}

.usage-badge {
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.usage-badge.used {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
}

.usage-badge.unused {
  background: linear-gradient(135deg, #ffc107 0%, #fd7e14 100%);
  color: #212529;
}

.header-actions {
  display: flex;
  align-items: center;
}

.unsaved-indicator {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
}

.list-edit-body {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
}

.tree-section {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 24px;
}

.tree-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f1f3f4;
}

.tree-header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.tree-header h4 {
  color: #2c3e50;
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
}

.usage-filter-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 0.9rem;
  color: #6c757d;
  margin: 0;
  white-space: nowrap;
}

.tree-help {
  color: #6c757d;
  font-size: 0.95rem;
  margin: 0;
  line-height: 1.5;
}

.tree-help-usage {
  display: block;
  margin-top: 4px;
  font-size: 0.85rem;
}

.loading-usage-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 16px;
  color: #6c757d;
  font-size: 0.9rem;
}

.tree-content-wrapper {
  min-height: 400px;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
  padding: 16px;
  background: #fafbfc;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.actions-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.action-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 24px;
}

.action-card h5 {
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
}

/* Scrollbar styling */
.tree-content-wrapper::-webkit-scrollbar {
  width: 8px;
}

.tree-content-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.tree-content-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.tree-content-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Responsive */
@media (max-width: 992px) {
  .list-edit-body {
    grid-template-columns: 1fr;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
